import { useEffect, useRef, useState, ReactNode } from 'react'

interface ResizableVerticalPanelProps {
  children: [ReactNode, ReactNode]
  defaultTopHeight?: number
  minTopHeight?: number
  maxTopHeight?: number
  storageKey?: string
  className?: string
}

export default function ResizableVerticalPanel({
  children,
  defaultTopHeight = 60,
  minTopHeight = 20,
  maxTopHeight = 80,
  storageKey,
  className = ''
}: ResizableVerticalPanelProps) {
  const [topHeight, setTopHeight] = useState<number>(() => {
    if (storageKey) {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const parsed = parseFloat(saved)
        if (parsed >= minTopHeight && parsed <= maxTopHeight) {
          return parsed
        }
      }
    }
    return defaultTopHeight
  })

  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(storageKey, topHeight.toString())
    }
  }, [topHeight, storageKey])

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDoubleClick = () => {
    setTopHeight(defaultTopHeight)
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return

      const container = containerRef.current
      const containerRect = container.getBoundingClientRect()
      const containerHeight = containerRect.height

      const newTopHeight = ((e.clientY - containerRect.top) / containerHeight) * 100

      if (newTopHeight >= minTopHeight && newTopHeight <= maxTopHeight) {
        setTopHeight(newTopHeight)
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, minTopHeight, maxTopHeight])

  const dividerHeight = 2

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden'
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: `calc(${topHeight}% - ${dividerHeight / 2}px)`,
          overflow: 'hidden'
        }}
      >
        {children[0]}
      </div>

      <div
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: `calc(${topHeight}% - ${dividerHeight / 2}px)`,
          height: `${dividerHeight}px`,
          cursor: 'row-resize',
          backgroundColor: isDragging ? 'var(--color-primary)' : 'var(--color-line)',
          transition: 'background-color 0.15s',
          zIndex: 10
        }}
        onMouseEnter={(e) => {
          if (!isDragging) {
            e.currentTarget.style.backgroundColor = 'var(--color-primary)'
          }
        }}
        onMouseLeave={(e) => {
          if (!isDragging) {
            e.currentTarget.style.backgroundColor = 'var(--color-line)'
          }
        }}
      />

      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: `calc(${100 - topHeight}% - ${dividerHeight / 2}px)`,
          overflow: 'hidden'
        }}
      >
        {children[1]}
      </div>
    </div>
  )
}
